import { useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  THERAPIST_PRIMARY_TABS,
  PARENT_PRIMARY_TABS,
  THERAPIST_MENU_GROUPS,
  PARENT_MENU_GROUPS,
} from './navConfig';
import MenuDrawer from './MenuDrawer';
import {
  CalendarDays, Library, BookOpen, Receipt, Settings, MessageCircle, Sparkles,
  ShieldCheck, FileText, Lock, Users, Users2, BarChart3, Heart, Bell, LogOut, MoreHorizontal,
  GraduationCap, Tag, ListTodo, School, Search, MailWarning,
} from 'lucide-react';

const ICON_MAP = {
  CalendarDays, Library, BookOpen, Receipt, Settings, MessageCircle, Sparkles,
  ShieldCheck, FileText, Lock, Users, Users2, BarChart3, Heart, Bell, GraduationCap, Tag, ListTodo, School, Search, MailWarning,
};

/**
 * Desktop-only persistent left sidebar (≥ md breakpoint).
 * Primary tabs at top, full menu groups expanded below.
 */
export default function SideNav({ user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const primaryTabs = user?.role === 'parent' ? PARENT_PRIMARY_TABS : THERAPIST_PRIMARY_TABS;
  const isSuperAdmin = !!user?.is_super_admin;
  const hasParentAppAccess = !!user?.has_parent_app_access;
  const groups =
    user?.role === 'parent' ? PARENT_MENU_GROUPS() : THERAPIST_MENU_GROUPS(isSuperAdmin, hasParentAppAccess);

  const isActive = (path) => {
    if (!path) return false;
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path.split('#')[0]);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  return (
    <>
      <aside
        className="hidden md:flex md:fixed md:inset-y-0 md:left-0 md:w-60 md:flex-col bg-[#FDFBF7] border-r border-stone-200 z-30"
        data-testid="side-nav"
      >
        {/* Brand */}
        <div className="px-6 pt-5 pb-3">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-brand">
            <Heart className="w-5 h-5 text-primary fill-primary/30" />
            <span className="font-serif text-xl text-primary-900">PlayFirst</span>
          </Link>
        </div>

        {/* Primary tabs */}
        <div className="px-3 space-y-0.5">
          {primaryTabs
            .filter((t) => !t.action) // skip the "Menu" virtual tab on desktop
            .map((tab) => {
              const Icon = tab.icon;
              const active = isActive(tab.path);
              return (
                <button
                  key={tab.id}
                  onClick={() => navigate(tab.path)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${
                    active ? 'bg-white shadow-sm text-primary-900 font-medium' : 'text-stone-700 hover:bg-white/60'
                  }`}
                  data-testid={`nav-${tab.id}`}
                >
                  <Icon className={`w-4 h-4 ${active ? 'text-primary' : 'text-stone-500'}`} />
                  <span className="text-[14px]">{tab.label}</span>
                </button>
              );
            })}
        </div>

        {/* Grouped menu */}
        <div className="flex-1 overflow-y-auto px-3 pt-4 pb-6 space-y-5">
          {groups.map((group) => (
            <div key={group.label}>
              <p className="px-3 mb-1 text-[10px] uppercase tracking-wider font-semibold text-stone-400">
                {group.label}
              </p>
              <ul className="space-y-0.5">
                {group.items.map((item) => {
                  const Icon = ICON_MAP[item.icon] || BookOpen;
                  const active = isActive(item.path);
                  return (
                    <li key={item.path + item.label}>
                      <button
                        onClick={() => {
                          if (item.external) {
                            window.open(item.path, '_blank', 'noopener,noreferrer');
                          } else {
                            navigate(item.path);
                          }
                        }}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                          active ? 'bg-white shadow-sm text-primary-900' : 'text-stone-700 hover:bg-white/60'
                        }`}
                        data-testid={`side-link-${item.path.replace(/[^a-z0-9]/gi, '-')}`}
                      >
                        <Icon className={`w-4 h-4 shrink-0 ${active ? 'text-primary' : 'text-stone-500'}`} />
                        <span className="text-[13px] truncate">{item.label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        {/* Account row */}
        <div className="border-t border-stone-200 p-3 bg-white/40">
          {user?.name && (
            <p className="px-3 pb-2 text-xs text-stone-500 truncate">
              {user.name}
              {user.role === 'parent' && ' (parent)'}
              {isSuperAdmin && ' (admin)'}
            </p>
          )}
          <button
            onClick={() => setDrawerOpen(true)}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-stone-700 hover:bg-white/60"
            data-testid="side-nav-more-btn"
          >
            <MoreHorizontal className="w-4 h-4 text-stone-500" />
            <span className="text-[13px]">More options</span>
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-red-700 hover:bg-red-50 mt-0.5"
            data-testid="side-nav-logout-btn"
          >
            <LogOut className="w-4 h-4 text-red-500" />
            <span className="text-[13px]">Sign out</span>
          </button>
        </div>
      </aside>
      <MenuDrawer open={drawerOpen} onOpenChange={setDrawerOpen} user={user} />
    </>
  );
}
